/**
 * Shared executor for bulk thread actions backed by batch REST endpoints.
 *
 * Batch endpoints accept up to a server-enforced cap of items per request and
 * return per-item results, so a bulk dashboard action consumes one rate-limit
 * token per chunk instead of one per thread. Selections larger than the cap
 * are split into chunks issued concurrently; a chunk-level transport failure
 * marks only that chunk's items as failed.
 */
import { chunkArray } from '#pplx/frontend/apps/ask/shared-helpers/chunkArray.js';
import { isLocalThreadId } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { logger } from '#pplx/frontend/logger/browser.js';
/** Matches BATCH_ACTION_MAX_ITEMS enforced by the backend batch endpoints. */
const BULK_THREAD_ACTION_CHUNK_SIZE = 64;
/** Matches BATCH_MARK_THREAD_AS_VIEWED_LIMIT; mark_viewed/mark_unread silently truncate above it. */
export const BULK_MARK_READ_CHUNK_SIZE = 32;
/**
 * Chunks for the batch thread endpoints, with local (on-device) and cloud
 * items never sharing a chunk: the desktop shell routes a request natively
 * iff its body references a local thread id, and a homogeneous chunk keeps
 * per-chunk failure accounting exact. On web the local partition is always
 * empty.
 */
export function chunkForNativeRouting(items, isLocal, chunkSize) {
    return [
        ...chunkArray(items.filter((item) => !isLocal(item)), chunkSize),
        ...chunkArray(items.filter(isLocal), chunkSize),
    ];
}
export async function runChunkedBulkAction({ contextUuids, chunkSize = BULK_THREAD_ACTION_CHUNK_SIZE, execute, }) {
    const chunks = chunkForNativeRouting([...new Set(contextUuids)], isLocalThreadId, chunkSize);
    const results = await Promise.allSettled(chunks.map((chunk) => execute(chunk)));
    const succeededContextUuids = [];
    const failedContextUuids = [];
    for (const [index, result] of results.entries()) {
        const chunk = chunks[index] ?? [];
        if (result.status === 'rejected') {
            failedContextUuids.push(...chunk);
            continue;
        }
        const failedInChunk = new Set(result.value.failed.map((failure) => failure.context_uuid));
        for (const contextUuid of chunk) {
            if (failedInChunk.has(contextUuid)) {
                failedContextUuids.push(contextUuid);
            }
            else {
                succeededContextUuids.push(contextUuid);
            }
        }
    }
    return { succeededContextUuids, failedContextUuids };
}
/**
 * Awaits a bulk action, converting an unexpected rejection (the chunked
 * executor itself never rejects) into an all-failed outcome so callers can
 * roll back and report uniformly.
 */
export async function settleBulkAction(contextUuids, run) {
    try {
        return await run();
    }
    catch (error) {
        logger.error(error);
        return { succeededContextUuids: [], failedContextUuids: [...contextUuids] };
    }
}
//# sourceMappingURL=bulkThreadActions.js.map